import { ChangeDetectionStrategy, Component } from '@angular/core'
import { MessageService } from 'primeng/api'

import {
  BACKGROUND_CATEGORY_LABELS,
  BACKGROUND_SCORE_SCALE,
  backgroundVerdict,
  BilHitView,
  bilHitView,
  BackgroundVerdict,
  formatOutOfTen,
  formatPesos,
  formatShortDate,
  humanizeCode,
  labelOf,
  LAWSUIT_CONFIDENCE_LABELS,
  LAWSUIT_ROLE_LABELS,
  PillTone,
  SEVERITY_TONE,
  SHAREHOLDER_SOURCE_LABELS,
} from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { BackgroundCategory, BilReport, CorporateAct, CorporateCompany, Lawsuit, LawsuitRole, LegalView, SnapshotTab } from '@data/interfaces/verification-snapshot.interface'
import { sectionStateText, SnapshotUiState } from '../snapshot-state/snapshot-state.component'
import { databaseCount } from '../../analysis-photo.util'
import { SnapshotTabBase } from '../snapshot-tab.base'

/** Una categoría de antecedentes lista para la tabla: la pastilla sale de la calificación de Syntage, no del conteo. */
export interface BackgroundRow {
  label: string
  verdict: BackgroundVerdict
  tone: PillTone
  score: string
  records: number
  databases: string
}

/** Una coincidencia de BIL para la tabla: lo que se compara en la fila y todos sus campos para el detalle. */
export interface BilRow extends BilHitView {
  code: string
  keyData: string
  grade: string
}

/** Filtro de expedientes por el papel del cliente. «Ambos» cuenta como demandado y como demandante. */
export type LawsuitFilter = 'all' | 'defendant' | 'plaintiff' | 'unconfirmed'

/**
 * Tono de la pastilla del papel en la tabla de expedientes (maqueta F-158 §8.3): demandado en rojo, demandante neutro
 * —demandar no es una alarma— y por confirmar en ámbar. «Ambos» incluye ser demandado, así que va en rojo.
 */
const ROLE_TONE: Record<LawsuitRole, PillTone> = {
  defendant: 'bad',
  both: 'bad',
  plaintiff: 'neutral',
  unconfirmed: 'warn',
}

/** Estado corto de una sección para el pie de su casilla en la franja. */
const STATE_SHORT: Partial<Record<SnapshotUiState, string>> = {
  empty: 'sin registros',
  pending: 'Syntage aún la procesa',
  unavailable: 'no disponible',
  'not-requested': 'no consultado',
  'source-error': 'error al consultar',
}

/**
 * Sub-pestaña «Legal y corporativo» (F-158). Solo se monta con `leases.view`.
 *
 * Es lo que Syntage encontró, no un dictamen: el subtítulo lo dice para que no se confunda con la
 * pestaña «Dictamen Legal» del expediente, que sí es el dictamen de Legal.
 *
 * ! El papel del cliente en cada expediente (`role`) lo deduce el backend con su RFC y su razón social
 * ! (`lawsuit-role.ts`). La «Actuación» del proveedor NO es el papel —en la muestra ent-b dice
 * ! «Demandante» y el cliente es el demandado—, así que va aparte y como texto secundario.
 */
@Component({
  selector: 'app-ciec-legal-tab',
  templateUrl: './ciec-legal-tab.component.html',
  styleUrls: ['./ciec-legal-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class CiecLegalTabComponent extends SnapshotTabBase<LegalView> {
  protected readonly tab: SnapshotTab = 'legal'

  backgroundRows: BackgroundRow[] = []
  /** Socios o administradores que también aparecen como demandados, sin repetir. */
  coDefendants: string[] = []
  /** Coincidencias de BIL con sus campos etiquetados. */
  bilHits: BilRow[] = []
  /** Filtro por papel de la tabla de expedientes. */
  lawsuitFilter: LawsuitFilter = 'all'
  /** Expedientes y coincidencias de BIL con el detalle abierto, por su llave. */
  readonly openRows = new Set<string>()
  /** «Los porcentajes reportados por Buró suman 150 %» cuando la participación no da 100 %; `null` si da o no hay porcentajes. */
  shareholdersTotalNote: string | null = null

  readonly humanize = humanizeCode
  readonly outOfTen = formatOutOfTen
  readonly shortDate = formatShortDate
  readonly stateShort = STATE_SHORT

  get sections(): LegalView['sections'] {
    return this.view?.sections ?? {}
  }

  /**
   * BIL no se pidió y quien mira puede buscarlo: se le dice dónde. La búsqueda de la pestaña Buró y deuda trae
   * también el BIL, así que aquí no hay un botón propio.
   */
  get suggestsBilSearch(): boolean {
    return this.sections.bil?.status === 'not_requested' && this.canAnnex
  }

  /** El bloque del PDF de BIL (botón o nota) se pinta si la foto registró su archivo: el id del PDF en Syntage no sale por la API. */
  get hasBilPdf(): boolean {
    return this.fileStatus('bil') !== null
  }

  /** Sociedades del Registro Público de Comercio: una tarjeta por empresa. */
  get companies(): CorporateCompany[] {
    return this.sectionState(this.sections.corporate) ? [] : (this.sections.corporate?.data?.companies ?? [])
  }

  /** Actos inscritos, a todo el ancho bajo la sociedad y los accionistas. */
  get acts(): CorporateAct[] {
    return this.sectionState(this.sections.corporate) ? [] : (this.sections.corporate?.data?.acts ?? [])
  }

  /**
   * El acto comparte documento con el anterior: el reductor lee las personas una sola vez por documento, así que las
   * suyas son las del acto de arriba. Sin documento no se puede afirmar.
   */
  sharesPreviousDocument(index: number): boolean {
    const current = this.acts[index]?.documentNumber
    return index > 0 && !!current && current === this.acts[index - 1]?.documentNumber
  }

  /** Monto de una persona del acto (capital suscrito), en pesos sin centavos. */
  money(value: number | null | undefined): string {
    return value === null || value === undefined || !Number.isFinite(value) ? '—' : formatPesos(value)
  }

  /** Poderes vigentes y los de la verificación de la empresa, en una sola lista. */
  get powerRecords(): Array<Record<string, unknown>> {
    const powers = this.sections.powers?.data
    return [...(powers?.currentPowers ?? []), ...(powers?.companyVerification ?? [])]
  }

  /** El papel; un código que el panel no conozca se trata como «Por confirmar». */
  roleOf(lawsuit: Lawsuit): (typeof LAWSUIT_ROLE_LABELS)[keyof typeof LAWSUIT_ROLE_LABELS] {
    return LAWSUIT_ROLE_LABELS[lawsuit.role] ?? LAWSUIT_ROLE_LABELS.unconfirmed
  }

  roleTone(lawsuit: Lawsuit): PillTone {
    return ROLE_TONE[lawsuit.role] ?? ROLE_TONE.unconfirmed
  }

  /** La otra parte del juicio según el papel del cliente; con el papel sin confirmar o en ambos lados no se deduce. */
  counterpartOf(lawsuit: Lawsuit): string {
    if (lawsuit.role === 'defendant') return lawsuit.plaintiff ?? '—'
    if (lawsuit.role === 'plaintiff') return lawsuit.defendant ?? '—'
    return '—'
  }

  /** Los expedientes de la foto, ya consultados. */
  get lawsuits(): Lawsuit[] {
    return this.sections.lawsuits?.status === 'complete' ? (this.sections.lawsuits.data?.items ?? []) : []
  }

  /** Cuántos expedientes caen en cada filtro. */
  get lawsuitCounts(): Record<LawsuitFilter, number> {
    return {
      all: this.lawsuits.length,
      defendant: this.lawsuits.filter((lawsuit) => this.matches(lawsuit, 'defendant')).length,
      plaintiff: this.lawsuits.filter((lawsuit) => this.matches(lawsuit, 'plaintiff')).length,
      unconfirmed: this.lawsuits.filter((lawsuit) => this.matches(lawsuit, 'unconfirmed')).length,
    }
  }

  get filteredLawsuits(): Lawsuit[] {
    return this.lawsuits.filter((lawsuit) => this.matches(lawsuit, this.lawsuitFilter))
  }

  readonly lawsuitFilters: Array<{ key: LawsuitFilter; label: string }> = [
    { key: 'all', label: 'Todos' },
    { key: 'defendant', label: 'Como demandado' },
    { key: 'plaintiff', label: 'Como demandante' },
    { key: 'unconfirmed', label: 'Por confirmar' },
  ]

  setLawsuitFilter(filter: LawsuitFilter): void {
    this.lawsuitFilter = filter
  }

  /** Llave del expediente por su lugar en la foto: no cambia al filtrar por papel. */
  lawsuitKey(lawsuit: Lawsuit): string {
    return `lawsuit:${this.lawsuits.indexOf(lawsuit)}`
  }

  toggleRow(key: string): void {
    if (this.openRows.has(key)) this.openRows.delete(key)
    else this.openRows.add(key)
  }

  /** El score de antecedentes en su escala de 10, sin el «/ 10» (va aparte, más chico). */
  get backgroundScoreValue(): string | null {
    const score = this.sections.background?.data?.score
    if (score === null || score === undefined || !Number.isFinite(score)) return null
    return (score * BACKGROUND_SCORE_SCALE).toLocaleString('es-MX', { maximumFractionDigits: 1 })
  }

  /** Categorías con la calificación rebajada por Syntage: las únicas que van en rojo. */
  get backgroundAtRisk(): number {
    return this.backgroundRows.filter((row) => row.tone === 'bad').length
  }

  readonly backgroundScale = BACKGROUND_SCORE_SCALE

  /** Accionistas sin porcentaje y las fuentes del dato, para el pie de su casilla en la franja. */
  get shareholderSummary(): { sources: string; withoutPercentage: number } | null {
    const items = this.sections.shareholders?.status === 'complete' ? (this.sections.shareholders.data?.items ?? []) : []
    if (!items.length) return null
    return { sources: [...new Set(items.map((holder) => this.sourceLabel(holder.source)))].join(' y '), withoutPercentage: items.filter((holder) => holder.percentage === null).length }
  }

  /** RPC y poderes sin datos bajan a «Sin registros:» con su estado. */
  get corporateWithoutRecords(): Array<{ key: 'corporate' | 'powers'; label: string; state: SnapshotUiState; text: string }> {
    const sources = [
      { key: 'corporate' as const, label: 'Datos corporativos (RPC)' },
      { key: 'powers' as const, label: 'Poderes vigentes' },
    ]
    return sources.flatMap(({ key, label }) => {
      const state = this.sectionState(this.sections[key])
      return state ? [{ key, label, state, text: sectionStateText(state, this.sections[key]?.reason) }] : []
    })
  }

  private matches(lawsuit: Lawsuit, filter: LawsuitFilter): boolean {
    const role = LAWSUIT_ROLE_LABELS[lawsuit.role] ? lawsuit.role : 'unconfirmed'
    if (filter === 'all') return true
    if (filter === 'defendant') return role === 'defendant' || role === 'both'
    if (filter === 'plaintiff') return role === 'plaintiff' || role === 'both'
    return role === 'unconfirmed'
  }

  confidenceLabel(lawsuit: Lawsuit): string {
    return labelOf(LAWSUIT_CONFIDENCE_LABELS, lawsuit.confidence)
  }

  sourceLabel(source: string): string {
    return labelOf(SHAREHOLDER_SOURCE_LABELS, source)
  }

  protected override onView(): void {
    const background = this.sections.background
    const lawsuits = this.sections.lawsuits?.status === 'complete' ? (this.sections.lawsuits.data?.items ?? []) : []
    //* Un juicio donde el cliente demanda no es una alarma: si es su papel en todos, judiciales lo dice así.
    const onlyAsPlaintiff = lawsuits.length > 0 && lawsuits.every((lawsuit) => lawsuit.role === 'plaintiff')
    this.backgroundRows = (background?.status === 'complete' ? (background.data?.categories ?? []) : []).map((category) =>
      this.backgroundRow(category, onlyAsPlaintiff && category.category === 'legal_background'),
    )
    const names = (this.sections.lawsuits?.data?.items ?? []).flatMap((lawsuit) => lawsuit.coDefendantPartners ?? [])
    this.coDefendants = [...new Set(names)]
    this.shareholdersTotalNote = this.totalNote()
    this.bilHits = (this.sections.bil?.data?.hits ?? []).map((hit) => this.bilRow(hit))
    //* El primer acto abre desplegado: es el más reciente y el que suele traer las personas.
    if (this.acts[0]?.people?.length) this.openRows.add('act:0')
  }

  /**
   * La API ya quita al accionista que el Buró repite y manda la suma (`percentageTotal`). Una foto anterior a eso no
   * la trae: ahí se suma lo que se pinta, repetidos incluidos, para que la nota cuadre con la tabla que se ve.
   */
  private totalNote(): string | null {
    const data = this.sections.shareholders?.status === 'complete' ? this.sections.shareholders.data : null
    if (!data?.items.length) return null
    const percentages = data.items.map((holder) => holder.percentage).filter((value): value is number => typeof value === 'number')
    const fromItems = percentages.length ? Math.round(percentages.reduce((sum, value) => sum + value, 0) * 100) / 100 : null
    const total = data.percentageTotal === undefined ? fromItems : data.percentageTotal
    if (total === null || total === 100) return null
    return `Los porcentajes reportados por ${this.sourceLabel(data.items[0].source)} suman ${total.toLocaleString('es-MX', { maximumFractionDigits: 2 })} %.`
  }

  private backgroundRow(category: BackgroundCategory, onlyAsPlaintiff: boolean): BackgroundRow {
    const { consulted, total } = databaseCount(category)
    const verdict = backgroundVerdict(category, onlyAsPlaintiff)
    return {
      label: labelOf(BACKGROUND_CATEGORY_LABELS, category.category),
      verdict,
      tone: SEVERITY_TONE[verdict.severity],
      score: formatOutOfTen(category.score),
      records: category.recordsCount ?? 0,
      databases: `${consulted} de ${total}`,
    }
  }

  /**
   * El «dato clave» de la fila: el importe de una coincidencia detallada (Compranet) o el juicio y el periodo de una
   * básica. El resto de los campos va en el detalle.
   */
  private bilRow(hit: BilReport['hits'][number]): BilRow {
    const amount = hit.detail?.['IMPORTE']
    const keyData = amount !== undefined && amount !== null && amount !== '' ? String(amount) : [hit.lawsuit, hit.period].filter((value) => !!value).join(' · ')
    return { ...bilHitView(hit), code: hit.code ?? '—', keyData: keyData || '—', grade: hit.grade ?? '—' }
  }

  trackByIndex(index: number): number {
    return index
  }
}
